import React from 'react';
import Tile from '../../design-system/Tile';
import { aggregateLaneStats } from '../../lib/analytics';
import { MONTH_MS } from '../../constants';

/**
 * APEX v7.0 — KPI Tiles Row
 *
 * Affiche les 4 KPIs principaux du Dashboard.
 *
 * v7.0 : 4 KPIs fixes (Rang, Delta session, Winrate 30j, Lane WR @15)
 * v7.2 : prévu — configurable par user via userSettings.dashboard_kpis
 *
 * Props:
 *   - games           : games filtered
 *   - rankSnapshots   : pour le rang actuel
 *   - lpDeltasByGame  : pour calculer delta session
 *   - userSettings    : (pour v7.2) configuration custom des KPIs
 */


function getCurrentRank(rankSnapshots) {
  if (!Array.isArray(rankSnapshots) || rankSnapshots.length === 0) return null;
  const latest = [...rankSnapshots].sort((a, b) =>
    new Date(b.captured_at) - new Date(a.captured_at)
  )[0];
  if (!latest) return null;
  return {
    tier: latest.tier,
    division: latest.division,
    lp: latest.lp,
    label: latest.tier && latest.division
      ? `${latest.tier.slice(0, 1).toUpperCase()}${latest.tier.slice(1).toLowerCase()} ${latest.division || ''}`.trim()
      : latest.tier
  };
}

/**
 * Delta jour : LP actuel - LP au dernier snapshot avant minuit aujourd'hui.
 * Si aucun snapshot avant minuit → compare au plus vieux snapshot du jour.
 */
function computeDayDelta(rankSnapshots, games) {
  const safeSnaps = Array.isArray(rankSnapshots) ? rankSnapshots : [];
  const safeGames = Array.isArray(games) ? games : [];

  // Minuit heure locale
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const midnightMs = midnight.getTime();

  // Trier les snapshots par date croissante
  const sorted = [...safeSnaps].sort(
    (a, b) => new Date(a.captured_at) - new Date(b.captured_at)
  );

  if (sorted.length === 0) return { delta: null, wins: 0, losses: 0, total: 0, currentLp: null };

  // LP actuel = dernier snapshot
  const latest = sorted[sorted.length - 1];
  const currentLp = latest.lp ?? 0;

  // LP de référence = dernier snapshot strictement avant minuit
  const beforeMidnight = sorted.filter(s => new Date(s.captured_at) < midnightMs);
  const refSnapshot = beforeMidnight.length > 0
    ? beforeMidnight[beforeMidnight.length - 1]  // le plus récent avant minuit
    : sorted[0];                                  // fallback : plus vieux du jour

  const refLp = refSnapshot.lp ?? 0;
  const delta = currentLp - refLp;

  // Games du jour pour W/L
  const todayGames = safeGames.filter(g => new Date(g.timestamp) >= midnightMs);
  const wins   = todayGames.filter(g => g.result === 'W').length;
  const losses = todayGames.filter(g => g.result === 'L').length;

  return { delta, wins, losses, total: todayGames.length, currentLp, refLp };
}

function computeWinrate(games, withinMs = MONTH_MS) {
  if (!Array.isArray(games) || games.length === 0) return { wr: 0, total: 0, wins: 0 };
  const cutoff = Date.now() - withinMs;
  const recent = games.filter(g => new Date(g.timestamp) > cutoff);
  if (recent.length === 0) return { wr: 0, total: 0, wins: 0 };
  const wins = recent.filter(g => g.result === 'W').length;
  return {
    wr: Math.round((wins / recent.length) * 100),
    total: recent.length,
    wins
  };
}

function computeWinrateChange(games) {
  if (!Array.isArray(games) || games.length === 0) return null;
  const now = Date.now();
  const last30 = games.filter(g => new Date(g.timestamp) > now - MONTH_MS);
  const prev30 = games.filter(g => {
    const t = new Date(g.timestamp).getTime();
    return t > now - 2 * MONTH_MS && t <= now - MONTH_MS;
  });

  if (last30.length === 0 || prev30.length === 0) return null;
  const lastWr = Math.round((last30.filter(g => g.result === 'W').length / last30.length) * 100);
  const prevWr = Math.round((prev30.filter(g => g.result === 'W').length / prev30.length) * 100);
  return lastWr - prevWr;
}

export default function KpiTilesRow({
  games = [],
  rankSnapshots = [],
  lpDeltasByGame = {}, // conservé pour GameHistorySidebar, non utilisé ici
  userSettings
}) {
  const safeGames = Array.isArray(games) ? games : [];

  // KPI 1 — Rang actuel
  const rank = getCurrentRank(rankSnapshots);

  // KPI 2 — Delta jour (LP minuit → maintenant)
  const dayDelta = computeDayDelta(rankSnapshots, safeGames);

  // KPI 3 — Winrate 30j
  const wr = computeWinrate(safeGames, MONTH_MS);
  const wrChange = computeWinrateChange(safeGames);

  // KPI 4 — Lane WR @ 15 (30 derniers jours)
  const cutoff = Date.now() - MONTH_MS;
  const recent30 = safeGames.filter(g => new Date(g.timestamp) > cutoff);
  const laneStats = aggregateLaneStats(recent30, 15, userSettings?.primary_role || 'ADC');

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* KPI 1 — Rang */}
      <Tile
        label="Rang"
        value={rank?.label || '—'}
        color="violet"
        subtext={rank ? `${rank.lp ?? 0} LP` : 'Importer pour voir'}
      />

      {/* KPI 2 — Delta jour */}
      <Tile
        label="Delta jour"
        value={dayDelta.delta !== null ? (dayDelta.delta > 0 ? `+${dayDelta.delta}` : dayDelta.delta) : '—'}
        unit={dayDelta.delta !== null ? 'LP' : ''}
        color={dayDelta.delta > 0 ? 'cyan' : dayDelta.delta < 0 ? 'rose' : 'white'}
        subtext={
          dayDelta.total > 0
            ? `${dayDelta.wins}W ${dayDelta.losses}L aujourd'hui`
            : dayDelta.delta !== null
              ? `Ref: ${dayDelta.refLp} LP à minuit`
              : 'Pas de snapshot'
        }
        trend={dayDelta.delta > 0 ? 'up' : dayDelta.delta < 0 ? 'down' : 'flat'}
      />

      {/* KPI 3 — Winrate 30j */}
      <Tile
        label="Winrate 30j"
        value={wr.total > 0 ? wr.wr : '—'}
        unit={wr.total > 0 ? '%' : ''}
        color={wr.wr >= 54 ? 'cyan' : wr.wr < 45 ? 'rose' : 'amber'}
        subtext={
          wr.total > 0
            ? wrChange !== null
              ? `vs ${wrChange > 0 ? '+' : ''}${wrChange}pt mois -1`
              : `${wr.wins}/${wr.total} games`
            : 'Pas de games 30j'
        }
        trend={wrChange > 0 ? 'up' : wrChange < 0 ? 'down' : 'flat'}
      />

      {/* KPI 4 — Lane WR @15 */}
      <Tile
        label="Lane WR @15"
        value={laneStats ? laneStats.laneWinrate : '—'}
        unit={laneStats ? '%' : ''}
        color={
          laneStats
            ? laneStats.laneWinrate >= 50
              ? 'cyan'
              : laneStats.laneWinrate < 35
                ? 'rose'
                : 'amber'
            : 'white'
        }
        subtext={
          laneStats
            ? `${laneStats.won}W ${laneStats.drawn}D ${laneStats.lost}L`
            : 'Données timeline manquantes'
        }
      />
    </div>
  );
}
